import {
  Button as ChakraButton,
  type ButtonProps as ChakraButtonProps,
} from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';

type Variant = 'solid' | 'outline' | 'ghost' | 'subtle';
type Palette = 'accent' | 'primary' | 'gray';

export interface FigmaButtonProps
  extends Omit<ChakraButtonProps, 'variant' | 'colorPalette'> {
  variant?: Variant;
  colorPalette?: Palette;
  children?: ReactNode;
}

/** Figma-styled button. Variants: solid / outline / ghost / subtle. */
export const Button = forwardRef<HTMLButtonElement, FigmaButtonProps>(
  function Button(
    { variant = 'solid', colorPalette = 'accent', size = 'sm', children, ...props },
    ref,
  ) {
    return (
      <ChakraButton
        ref={ref}
        size={size}
        fontWeight="500"
        borderRadius="md"
        transition="all 0.15s ease"
        {...getButtonStyles(variant, colorPalette)}
        {...props}
      >
        {children}
      </ChakraButton>
    );
  },
);

function getButtonStyles(variant: Variant, palette: Palette): ChakraButtonProps {
  const palettes: Record<Palette, Record<Variant, ChakraButtonProps>> = {
    accent: {
      solid: {
        bg: 'figma.accent',
        color: 'white',
        _hover: { bg: 'figma.accentHover' },
        _active: { bg: 'figma.accentHover', transform: 'scale(0.98)' },
      },
      outline: {
        bg: 'transparent',
        color: 'figma.accent',
        borderWidth: '1px',
        borderColor: 'figma.accent',
        _hover: { bg: 'figma.accentMuted' },
      },
      ghost: {
        bg: 'transparent',
        color: 'figma.accent',
        _hover: { bg: 'figma.accentMuted' },
      },
      subtle: {
        bg: 'figma.accentMuted',
        color: 'figma.accent',
        _hover: { bg: 'figma.accent', color: 'white' },
      },
    },
    primary: {
      solid: {
        bg: 'figma.primary',
        color: 'white',
        _hover: { bg: 'figma.primaryHover' },
        _active: { bg: 'figma.primaryHover', transform: 'scale(0.98)' },
      },
      outline: {
        bg: 'transparent',
        color: 'figma.primary',
        borderWidth: '1px',
        borderColor: 'figma.primary',
        _hover: { bg: 'figma.primaryMuted' },
      },
      ghost: {
        bg: 'transparent',
        color: 'figma.primary',
        _hover: { bg: 'figma.primaryMuted' },
      },
      subtle: {
        bg: 'figma.primaryMuted',
        color: 'figma.primary',
        _hover: { bg: 'figma.primary', color: 'white' },
      },
    },
    gray: {
      solid: {
        bg: 'figma.panelHover',
        color: 'figma.text',
        _hover: { bg: 'figma.borderLight' },
      },
      outline: {
        bg: 'transparent',
        color: 'figma.textSecondary',
        borderWidth: '1px',
        borderColor: 'figma.border',
        _hover: { bg: 'figma.panel', borderColor: 'figma.borderLight' },
      },
      ghost: {
        bg: 'transparent',
        color: 'figma.textSecondary',
        _hover: { bg: 'figma.panel', color: 'figma.text' },
      },
      subtle: {
        bg: 'figma.panel',
        color: 'figma.textSecondary',
        _hover: { bg: 'figma.panelHover', color: 'figma.text' },
      },
    },
  };

  return palettes[palette][variant];
}
